import React, { useState } from 'react';
import { apiClient, FolderCreate } from '../services/api';
import './NewProjectModal.css';

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated: () => void;
}

export const NewProjectModal: React.FC<NewProjectModalProps> = ({ isOpen, onClose, onProjectCreated }) => {
  const [formData, setFormData] = useState<FolderCreate>({ name: '', description: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await apiClient.createFolder({
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        folder_type: 'test_case',
      });
      setFormData({ name: '', description: '' });
      onProjectCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '' });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={handleClose}></div>
      <div className="modal-wrapper">
        <div className="modal-box">
          <div className="modal-header">
            <h2>Create New Project</h2>
            <button className="modal-close" onClick={handleClose} disabled={isLoading}>
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="modal-body">
            {error && <div className="modal-error">{error}</div>}

            <div className="form-field">
              <label htmlFor="project-name">Project Name *</label>
              <input
                id="project-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter project name"
                disabled={isLoading}
                required
                autoFocus
              />
            </div>

            <div className="form-field">
              <label htmlFor="project-description">Description</label>
              <textarea
                id="project-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add a description..."
                disabled={isLoading}
                rows={4}
              />
            </div>
          </form>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-create"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
